/**
 * Living Crystals — Astro AI Premium Experience
 * Full-screen flow: Welcome/Paywall → Birth Form → Timed Chat
 */
(function () {
  'use strict';

  const PROD_API = 'https://living-crystals.vercel.app/api';
  const LOCAL_API = 'http://localhost:5000/api';

  function resolveApiBase() {
    const configured =
      typeof window !== 'undefined' && window.LC_ASTRO_AI && window.LC_ASTRO_AI.apiBase;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalDev = host === '127.0.0.1' || host === 'localhost';

    if (isLocalDev) {
      return LOCAL_API;
    }
    if (configured && !String(configured).includes('localhost')) {
      return configured;
    }
    return PROD_API;
  }

  const API_BASE = resolveApiBase();
  const THEME_CFG =
    typeof window !== 'undefined' && window.LC_ASTRO_AI ? window.LC_ASTRO_AI : {};
  const AVATAR_URL = THEME_CFG.avatarUrl || '';
  const THEME_UPI_QR_URL = THEME_CFG.upiQrUrl || '';
  const STORAGE = {
    session: 'lc_astro_session_id',
    profile: 'lc_astro_profile',
    messages: 'lc_astro_messages_v1',
  };

  const CHAT_TIMEOUT_MS = 60000;
  const ERROR_FALLBACK = 'Something went wrong — please try again in a moment.';
  const ADVISOR_OFFLINE_MSG = 'Advisor is offline, try again.';

  async function readJsonResponse(res) {
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('application/json')) {
      const err = new Error(ADVISOR_OFFLINE_MSG);
      err.code = 'advisor_offline';
      throw err;
    }
    try {
      return await res.json();
    } catch {
      const err = new Error(ADVISOR_OFFLINE_MSG);
      err.code = 'advisor_offline';
      throw err;
    }
  }

  async function tryReadJsonResponse(res) {
    try {
      return await readJsonResponse(res);
    } catch (err) {
      if (err.code === 'advisor_offline') return null;
      throw err;
    }
  }

  const CONCERNS = [
    'Career',
    'Love & Marriage',
    'Health',
    'Wealth',
    'Gemstone Recommendation',
  ];

  const FEATURES = [
    { icon: '✦', title: 'Instant Vedic Kundali Reading', desc: 'Personalised chart insights in minutes' },
    { icon: '◈', title: 'Career, Love & Financial Guidance', desc: 'Clarity for life\'s pivotal decisions' },
    { icon: '◇', title: 'Living Crystals Gemstone Match', desc: 'Authentic stones aligned to your stars' },
  ];

  let state = {
    view: 'welcome',
    sessionId: localStorage.getItem(STORAGE.session) || crypto.randomUUID(),
    profile: null,
    messages: [],
    config: {
      sessionPriceInr: Number(THEME_CFG.sessionPriceInr) || 100,
      freeTrialMinutes: Number(THEME_CFG.freeTrialMinutes) || 5,
      paidSessionMinutes: Number(THEME_CFG.paidSessionMinutes) || 10,
      totalSessionMinutes:
        (Number(THEME_CFG.freeTrialMinutes) || 5) + (Number(THEME_CFG.paidSessionMinutes) || 10),
      shopBaseUrl: THEME_CFG.shopBaseUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
      upiQrImageUrl: THEME_UPI_QR_URL,
      upiId: THEME_CFG.upiId || '',
      streamingEnabled: true,
      realAiEnabled: true,
    },
    timerInterval: null,
    chatLocked: false,
    sessionPhase: 'none',
    paymentStatus: null,
    isSending: false,
    initInProgress: false,
    welcomeSent: false,
    resolvedQrUrl: '',
    sessionReady: false,
  };

  localStorage.setItem(STORAGE.session, state.sessionId);

  function hasCompleteProfile(profile) {
    const p = profile || state.profile;
    if (!p) return false;
    return !!(
      p.fullName &&
      p.dateOfBirth &&
      p.timeOfBirth &&
      p.phone &&
      p.email
    );
  }

  function createMessage(role, content) {
    return {
      id: crypto.randomUUID(),
      role,
      content: String(content || ''),
      timestamp: Date.now(),
    };
  }

  function normalizeMessages(raw) {
    return (raw || [])
      .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && m.content)
      .map((m) => ({
        id: m.id || crypto.randomUUID(),
        role: m.role,
        content: String(m.content),
        timestamp: m.timestamp || Date.now(),
      }));
  }

  function persistMessagesCache() {
    try {
      localStorage.setItem(
        STORAGE.messages,
        JSON.stringify({ sessionId: state.sessionId, messages: state.messages })
      );
    } catch {
      /* quota exceeded — server is source of truth */
    }
  }

  function loadMessagesCache() {
    try {
      const raw = localStorage.getItem(STORAGE.messages);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.sessionId !== state.sessionId) return null;
      return normalizeMessages(data.messages);
    } catch {
      return null;
    }
  }

  function hasAssistantWelcome() {
    return state.messages.some((m) => m.role === 'assistant');
  }

  let els = {};

  function avatarImg(className, alt) {
    if (!AVATAR_URL) {
      return `<div class="lc-astro-ai__avatar ${className || ''}" aria-hidden="true">🙏</div>`;
    }
    return `<img class="lc-astro-ai__avatar-img ${className || ''}" src="${escapeHtml(AVATAR_URL)}" alt="${escapeHtml(alt || 'Acharya Ji')}" width="48" height="48" loading="lazy" decoding="async">`;
  }

  /* ─── DOM ─── */
  function createApp() {
    const root = document.createElement('div');
    root.id = 'lc-astro-ai';
    root.className = 'lc-astro-ai';
    root.innerHTML = `
      <div class="lc-astro-ai__shell" role="dialog" aria-modal="true" aria-label="Astro AI">
        <button type="button" class="lc-astro-ai__close" data-action="close" aria-label="Close">&times;</button>

        <!-- PAGE 1: Welcome & Paywall -->
        <section class="lc-astro-ai__page lc-astro-ai__page--welcome" data-page="welcome">
          <div class="lc-astro-ai__badge">Powered by Living Crystals | Vedic Gemstone Insights</div>
          <div class="lc-astro-ai__hero-visual">
            <div class="lc-astro-ai__hero-portrait" aria-hidden="true">
              ${avatarImg('lc-astro-ai__avatar-img--hero', 'Acharya Ji')}
              <div class="lc-astro-ai__hero-portrait-ring"></div>
              <div class="lc-astro-ai__hero-portrait-glow"></div>
            </div>
          </div>
          <h1 class="lc-astro-ai__headline">Unlock Your Personal Kundali &amp; Gemstone Alignment with <span>Acharya Ji</span></h1>
          <div class="lc-astro-ai__features">
            ${FEATURES.map(
              (f) => `
              <article class="lc-astro-ai__feature-card">
                <span class="lc-astro-ai__feature-icon">${f.icon}</span>
                <h3>${f.title}</h3>
                <p>${f.desc}</p>
              </article>`
            ).join('')}
          </div>
          <div class="lc-astro-ai__pricing" id="lc-astro-ai-pricing">
            <div class="lc-astro-ai__pricing-hero">
              <span class="lc-astro-ai__pricing-free-badge" id="lc-astro-ai-free-badge">First 5 Minutes FREE</span>
              <span class="lc-astro-ai__pricing-amount" id="lc-astro-ai-pricing-amount">₹100 for 10 Additional Minutes</span>
              <span class="lc-astro-ai__pricing-tag">Real-time Vedic Consultation with Acharya Ji</span>
            </div>
            <ul class="lc-astro-ai__pricing-details" id="lc-astro-ai-pricing-details">
              <li><span class="lc-astro-ai__pricing-check">✓</span> First 5 minutes — completely free</li>
              <li><span class="lc-astro-ai__pricing-check">✓</span> Pay ₹100 via UPI for 10 additional minutes</li>
              <li><span class="lc-astro-ai__pricing-check">✓</span> Personal Kundali &amp; gemstone guidance</li>
              <li><span class="lc-astro-ai__pricing-check">✓</span> Authentic Living Crystals recommendations</li>
            </ul>
            <button type="button" class="lc-astro-ai__cta-gold" data-action="start-free" id="lc-astro-ai-start-btn">
              Start Free Consultation
            </button>
            <p class="lc-astro-ai__pricing-note" id="lc-astro-ai-pricing-note">
              No payment required to begin. Pay ₹100 via UPI after your free 5 minutes to continue for 10 more minutes.
            </p>
          </div>
        </section>

        <!-- PAGE 2: Your details (required before chat) -->
        <section class="lc-astro-ai__page lc-astro-ai__page--form hidden" data-page="form">
          <div class="lc-astro-ai__badge">Powered by Living Crystals | Vedic Gemstone Insights</div>
          <h2 class="lc-astro-ai__form-title">Before We Begin</h2>
          <p class="lc-astro-ai__form-sub">Please share a few details so Acharya Ji can guide you personally. First 5 minutes are free.</p>
          <form class="lc-astro-ai__form" id="lc-astro-ai-form" novalidate>
            <label class="lc-astro-ai__field">
              <span>Full Name</span>
              <input type="text" name="fullName" required placeholder="Your full name" autocomplete="name">
            </label>
            <label class="lc-astro-ai__field">
              <span>Date of Birth</span>
              <input type="text" name="dateOfBirth" required placeholder="DD/MM/YYYY" pattern="\\d{2}/\\d{2}/\\d{4}">
            </label>
            <label class="lc-astro-ai__field">
              <span>Time of Birth</span>
              <input type="text" name="timeOfBirth" required placeholder="e.g. 4:00 PM">
            </label>
            <label class="lc-astro-ai__field">
              <span>City of Birth</span>
              <input type="text" name="placeOfBirth" placeholder="City, State (helps for accurate reading)">
            </label>
            <label class="lc-astro-ai__field">
              <span>Phone Number</span>
              <input type="tel" name="phone" required placeholder="10-digit mobile number" autocomplete="tel">
            </label>
            <label class="lc-astro-ai__field">
              <span>Email</span>
              <input type="email" name="email" required placeholder="your@email.com" autocomplete="email">
            </label>
            <label class="lc-astro-ai__field">
              <span>What do you want to discuss? (optional)</span>
              <select name="concern">
                <option value="">Select if you like — or tell Acharya Ji in chat</option>
                ${CONCERNS.map((c) => `<option value="${c}">${c}</option>`).join('')}
              </select>
            </label>
            <button type="submit" class="lc-astro-ai__cta-gold">Start Chat with Acharya Ji</button>
          </form>
        </section>

        <!-- PAGE 3: Chat -->
        <section class="lc-astro-ai__page lc-astro-ai__page--chat hidden" data-page="chat">
          <header class="lc-astro-ai__chat-header">
            <div class="lc-astro-ai__agent">
              ${avatarImg('lc-astro-ai__avatar-img--header', 'Acharya Ji')}
              <div>
                <strong>Acharya Ji</strong>
                <span class="lc-astro-ai__online"><span></span> Online</span>
              </div>
            </div>
            <div class="lc-astro-ai__timer-wrap">
              <div class="lc-astro-ai__timer" id="lc-astro-ai-timer" aria-live="polite">Free chat: 05:00 remaining</div>
            </div>
          </header>
          <div class="lc-astro-ai__messages" id="lc-astro-ai-messages" aria-live="polite"></div>
          <form class="lc-astro-ai__chat-form" id="lc-astro-ai-chat-form">
            <textarea id="lc-astro-ai-input" rows="1" placeholder="Type your answer or ask a question..." required></textarea>
            <button type="submit" id="lc-astro-ai-send">Send</button>
          </form>
        </section>

        <!-- Payment / lock overlay (conversation stays visible underneath) -->
        <div class="lc-astro-ai__expired hidden" id="lc-astro-ai-expired">
          <div class="lc-astro-ai__expired-card lc-astro-ai__payment-card">
            ${avatarImg('lc-astro-ai__avatar-img--header', 'Acharya Ji')}

            <div id="lc-astro-pay-view" class="lc-astro-ai__pay-view">
              <span class="lc-astro-ai__pay-badge">Your free time has ended</span>
              <h3 id="lc-astro-pay-title">Pay to Continue</h3>
              <p class="lc-astro-ai__pay-amount" id="lc-astro-pay-amount">₹100</p>
              <p id="lc-astro-pay-text" class="lc-astro-ai__pay-instruction">Scan the QR code below with PhonePe, Google Pay, or any UPI app. After paying, tap <strong>Payment Done</strong>.</p>
              <div class="lc-astro-ai__qr-wrap" id="lc-astro-ai-qr-wrap">
                <img id="lc-astro-ai-qr" class="lc-astro-ai__qr" src="" alt="UPI QR code — scan to pay" width="280" height="280">
                <p class="lc-astro-ai__qr-missing hidden" id="lc-astro-ai-qr-missing">QR code not uploaded yet. Go to <strong>Theme settings → Living Crystals – Astro AI</strong> and upload your UPI QR code.</p>
                <p class="lc-astro-ai__upi-id hidden" id="lc-astro-ai-upi-id"></p>
              </div>
              <button type="button" class="lc-astro-ai__cta-gold lc-astro-ai__payment-done-btn" id="lc-astro-payment-done-btn">Payment Done</button>
              <button type="button" class="lc-astro-ai__link-btn" data-action="toggle-payment-details" id="lc-astro-toggle-payment-details">Add UTR / screenshot (optional, faster verification)</button>
              <form id="lc-astro-ai-payment-form" class="lc-astro-ai__payment-form hidden">
                <label class="lc-astro-ai__field">
                  <span>UPI Transaction / UTR / Reference Number</span>
                  <input type="text" name="utrReference" placeholder="Enter UTR or reference number" autocomplete="off">
                </label>
                <label class="lc-astro-ai__field">
                  <span>Payment Screenshot</span>
                  <input type="file" name="screenshot" accept="image/*">
                </label>
                <button type="submit" class="lc-astro-ai__cta-gold lc-astro-ai__cta-gold--secondary" id="lc-astro-ai-submit-payment">Submit with UTR &amp; Screenshot</button>
              </form>
            </div>

            <div id="lc-astro-pending-view" class="lc-astro-ai__pay-view hidden">
              <span class="lc-astro-ai__pay-badge lc-astro-ai__pay-badge--pending">Verification in progress</span>
              <h3>Thank you 🙏</h3>
              <p>We received your payment confirmation. Our team is verifying it now.</p>
              <p class="lc-astro-ai__pay-note">This usually takes a few minutes. Keep this window open — your chat will unlock automatically once approved.</p>
              <div class="lc-astro-ai__pending-spinner" aria-hidden="true"></div>
            </div>

            <div id="lc-astro-done-view" class="lc-astro-ai__pay-view hidden">
              <h3>Session Complete</h3>
              <p id="lc-astro-done-text">Your consultation time has ended. Thank you for speaking with Acharya Ji.</p>
            </div>

            <button type="button" class="lc-astro-ai__link-btn" data-action="close">Close</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(root);

    els = {
      root,
      pages: {
        welcome: root.querySelector('[data-page="welcome"]'),
        form: root.querySelector('[data-page="form"]'),
        chat: root.querySelector('[data-page="chat"]'),
      },
      form: root.querySelector('#lc-astro-ai-form'),
      chatForm: root.querySelector('#lc-astro-ai-chat-form'),
      messages: root.querySelector('#lc-astro-ai-messages'),
      input: root.querySelector('#lc-astro-ai-input'),
      send: root.querySelector('#lc-astro-ai-send'),
      timer: root.querySelector('#lc-astro-ai-timer'),
      expired: root.querySelector('#lc-astro-ai-expired'),
      paymentForm: root.querySelector('#lc-astro-ai-payment-form'),
      paymentDoneBtn: root.querySelector('#lc-astro-payment-done-btn'),
      qrImg: root.querySelector('#lc-astro-ai-qr'),
    };

    root.querySelector('[data-action="close"]').addEventListener('click', closeApp);
    root.querySelector('[data-action="start-free"]')?.addEventListener('click', () => {
      if (hasCompleteProfile(loadProfile())) {
        startChatSession();
      } else {
        showView('form');
        prefillFormFromProfile();
      }
    });
    els.form.addEventListener('submit', onFormSubmit);
    els.chatForm.addEventListener('submit', onChatSubmit);
    els.paymentForm?.addEventListener('submit', onPaymentSubmit);
    els.paymentDoneBtn?.addEventListener('click', onPaymentDone);
    root.querySelector('[data-action="toggle-payment-details"]')?.addEventListener('click', togglePaymentDetails);
    els.input.addEventListener('input', updateSendButtonState);
    els.input.addEventListener('keydown', onInputKeydown);
    root.querySelector('#lc-astro-ai-expired [data-action="close"]')?.addEventListener('click', closeApp);
    document.addEventListener('keydown', onKeydown);
    updateSendButtonState();
  }

  function updateSendButtonState() {
    if (!els.send || !els.input) return;
    const empty = !els.input.value.trim();
    els.send.disabled = state.chatLocked || state.isSending || empty;
  }

  function onInputKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!els.send.disabled) els.chatForm.requestSubmit();
    }
  }

  function scrollToBottom() {
    if (els.messages) els.messages.scrollTop = els.messages.scrollHeight;
  }

  function findMessageById(id) {
    return state.messages.find((m) => m.id === id);
  }

  function messageExists(id) {
    return !!findMessageById(id);
  }

  function setMessages(nextMessages, opts = {}) {
    state.messages = normalizeMessages(nextMessages);
    state.welcomeSent = hasAssistantWelcome();
    if (opts.render !== false) renderAllMessages();
    persistMessagesCache();
  }

  function appendMessage(msg, opts = {}) {
    const normalized = msg.id ? msg : createMessage(msg.role, msg.content);
    if (messageExists(normalized.id)) return null;
    state.messages.push(normalized);
    if (normalized.role === 'assistant') state.welcomeSent = true;
    if (opts.render !== false) renderMessageBubble(normalized);
    persistMessagesCache();
    scrollToBottom();
    return normalized;
  }

  function updateMessageContent(id, content) {
    const msg = findMessageById(id);
    if (msg) msg.content = content;
    const bubble = els.messages.querySelector(`[data-msg-id="${id}"] .lc-astro-ai__bubble, [data-msg-id="${id}"].lc-astro-ai__bubble`);
    if (bubble) bubble.textContent = content;
    scrollToBottom();
  }

  function renderMessageBubble(msg) {
    if (els.messages.querySelector(`[data-msg-id="${msg.id}"]`)) return;

    if (msg.role === 'assistant') {
      const row = document.createElement('div');
      row.className = 'lc-astro-ai__msg-row lc-astro-ai__msg-row--assistant';
      row.dataset.msgId = msg.id;
      row.innerHTML = `${avatarImg('lc-astro-ai__avatar-img--bubble', 'Acharya Ji')}<div class="lc-astro-ai__bubble lc-astro-ai__bubble--assistant"></div>`;
      row.querySelector('.lc-astro-ai__bubble').textContent = msg.content;
      els.messages.appendChild(row);
      return;
    }

    const row = document.createElement('div');
    row.className = 'lc-astro-ai__msg-row lc-astro-ai__msg-row--user';
    row.dataset.msgId = msg.id;
    const bubble = document.createElement('div');
    bubble.className = 'lc-astro-ai__bubble lc-astro-ai__bubble--user';
    bubble.textContent = msg.content;
    row.appendChild(bubble);
    els.messages.appendChild(row);
  }

  function renderAllMessages() {
    els.messages.innerHTML = '';
    state.messages.forEach((msg) => renderMessageBubble(msg));
    scrollToBottom();
  }

  function syncMessagesFromServer(serverMessages) {
    const normalized = normalizeMessages(serverMessages);
    if (!normalized.length) return;
    if (normalized.length >= state.messages.length) {
      setMessages(normalized);
    }
  }

  function showView(view) {
    state.view = view;
    Object.entries(els.pages).forEach(([key, page]) => {
      page.classList.toggle('hidden', key !== view);
    });
  }

  function normalizeAssetUrl(url) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    return trimmed;
  }

  function qrCandidateUrls() {
    const url = normalizeAssetUrl(state.config.upiQrImageUrl || THEME_UPI_QR_URL || '');
    return url ? [url] : [];
  }

  function probeImageUrl(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  async function resolveWorkingQrUrl() {
    for (const url of qrCandidateUrls()) {
      if (await probeImageUrl(url)) return url;
    }
    return '';
  }

  function qrImageUrl() {
    return state.resolvedQrUrl || normalizeAssetUrl(state.config.upiQrImageUrl || THEME_UPI_QR_URL || '');
  }

  async function refreshPaymentQr() {
    if (!els.qrImg) return;
    const missing = els.root.querySelector('#lc-astro-ai-qr-missing');
    const working = await resolveWorkingQrUrl();
    state.resolvedQrUrl = working;

    if (working) {
      els.qrImg.onerror = function () {
        els.qrImg.classList.add('hidden');
        missing?.classList.remove('hidden');
      };
      els.qrImg.onload = function () {
        els.qrImg.classList.remove('hidden');
        missing?.classList.add('hidden');
      };
      els.qrImg.src = working;
      els.qrImg.closest('#lc-astro-ai-qr-wrap')?.classList.remove('hidden');
      return;
    }

    els.qrImg.classList.add('hidden');
    els.qrImg.removeAttribute('src');
    missing?.classList.remove('hidden');
    els.qrImg.closest('#lc-astro-ai-qr-wrap')?.classList.remove('hidden');
  }

  function formatTimer(seconds) {
    const remaining = Math.max(0, Number(seconds) || 0);
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  function updateTimerDisplay(remainingSeconds, phase) {
    if (!els.timer) return;
    const time = formatTimer(remainingSeconds);
    const secs = Math.max(0, Number(remainingSeconds) || 0);

    els.timer.classList.remove('lc-astro-ai__timer--urgent');

    if (phase === 'paid') {
      els.timer.textContent = `Paid chat: ${time} remaining`;
    } else if (phase === 'free') {
      els.timer.textContent = `Free chat: ${time} remaining`;
      if (secs <= 60) els.timer.classList.add('lc-astro-ai__timer--urgent');
    } else if (secs > 0) {
      els.timer.textContent = `Session: ${time} remaining`;
      els.timer.classList.add('lc-astro-ai__timer--urgent');
    } else {
      els.timer.textContent = 'Free chat: 00:00 remaining';
      els.timer.classList.add('lc-astro-ai__timer--urgent');
    }
  }

  function updatePricingUI() {
    const { sessionPriceInr, freeTrialMinutes, paidSessionMinutes, upiId } = state.config;

    const freeBadge = els.root.querySelector('#lc-astro-ai-free-badge');
    const amount = els.root.querySelector('#lc-astro-ai-pricing-amount');
    const note = els.root.querySelector('#lc-astro-ai-pricing-note');
    const startBtn = els.root.querySelector('#lc-astro-ai-start-btn');
    const details = els.root.querySelector('#lc-astro-ai-pricing-details');
    const payText = els.root.querySelector('#lc-astro-pay-text');
    const payAmount = els.root.querySelector('#lc-astro-pay-amount');
    const payOffer = els.root.querySelector('#lc-astro-pay-offer');
    const upiIdEl = els.root.querySelector('#lc-astro-ai-upi-id');

    if (upiIdEl) {
      if (upiId) {
        upiIdEl.textContent = `UPI ID: ${upiId}`;
        upiIdEl.classList.remove('hidden');
      } else {
        upiIdEl.textContent = '';
        upiIdEl.classList.add('hidden');
      }
    }

    if (freeBadge) freeBadge.textContent = `First ${freeTrialMinutes} Minutes FREE`;
    if (amount) amount.textContent = `₹${sessionPriceInr} for ${paidSessionMinutes} Additional Minutes`;
    if (note) {
      note.textContent = `No payment required to begin. Pay ₹${sessionPriceInr} via UPI after your free ${freeTrialMinutes} minutes to continue for ${paidSessionMinutes} more minutes.`;
    }
    if (startBtn) startBtn.textContent = `Start Free Consultation (${freeTrialMinutes} min)`;
    if (payText) {
      payText.innerHTML = `Scan the QR code below with PhonePe, Google Pay, or any UPI app. Pay <strong>₹${sessionPriceInr}</strong> to get ${paidSessionMinutes} more minutes. Then tap <strong>Payment Done</strong>.`;
    }
    if (payAmount) payAmount.textContent = `₹${sessionPriceInr}`;
    if (payOffer) {
      payOffer.textContent = `Pay ₹${sessionPriceInr} via UPI — get ${paidSessionMinutes} additional minutes.`;
    }
    if (details) {
      details.innerHTML = `
        <li><span class="lc-astro-ai__pricing-check">✓</span> First ${freeTrialMinutes} minutes — completely free</li>
        <li><span class="lc-astro-ai__pricing-check">✓</span> Pay ₹${sessionPriceInr} via UPI for ${paidSessionMinutes} more minutes</li>
        <li><span class="lc-astro-ai__pricing-check">✓</span> Personal Kundali &amp; gemstone guidance</li>
        <li><span class="lc-astro-ai__pricing-check">✓</span> Authentic Living Crystals recommendations</li>`;
    }
    if (els.qrImg) {
      refreshPaymentQr();
    }
  }

  async function preparePaymentOverlay(view) {
    showPaymentView(view);
    els.expired.classList.remove('hidden');
    try {
      await fetchConfig();
    } catch {
      /* keep existing config */
    }
    await refreshPaymentQr();
  }

  function showPaymentView(view) {
    ['lc-astro-pay-view', 'lc-astro-pending-view', 'lc-astro-done-view'].forEach((id) => {
      els.root.querySelector('#' + id)?.classList.add('hidden');
    });
    const map = { pay: 'lc-astro-pay-view', pending: 'lc-astro-pending-view', done: 'lc-astro-done-view' };
    els.root.querySelector('#' + map[view])?.classList.remove('hidden');
  }

  function setChatLocked(locked) {
    state.chatLocked = locked;
    els.input.disabled = locked;
    updateSendButtonState();
  }

  function applySessionStatus(data, opts = {}) {
    if (!data) return;

    state.sessionPhase = data.phase;
    state.paymentStatus = data.paymentStatus;
    updateTimerDisplay(data.remainingSeconds, data.phase);

    if (data.birthDetails) {
      state.profile = data.birthDetails;
    }

    if (opts.syncMessages && Array.isArray(data.messages) && data.messages.length) {
      syncMessagesFromServer(data.messages);
    }

    if (data.allowed) {
      setChatLocked(false);
      els.expired.classList.add('hidden');
      return;
    }

    setChatLocked(true);

    // Payment/lock overlays only when customer is already in the chat view
    if (state.view !== 'chat') {
      els.expired.classList.add('hidden');
      return;
    }

    if (data.phase === 'pending_verification' || data.paymentStatus === 'pending') {
      preparePaymentOverlay('pending');
      return;
    }

    if (data.phase === 'paid_expired') {
      preparePaymentOverlay('done');
      return;
    }

    if (data.phase === 'free_expired' || data.phase === 'locked') {
      preparePaymentOverlay('pay');
    }
  }

  async function syncSessionStatus(opts = {}) {
    try {
      const res = await fetch(`${API_BASE}/session/${encodeURIComponent(state.sessionId)}/status`);
      if (res.status === 404) return null;
      const data = await tryReadJsonResponse(res);
      if (!data) return null;
      if (res.ok && opts.apply !== false) applySessionStatus(data);
      return data;
    } catch {
      return null;
    }
  }

  function startStatusPolling() {
    stopStatusPolling();
    state.timerInterval = setInterval(syncSessionStatus, 1000);
    syncSessionStatus();
  }

  function stopStatusPolling() {
    if (state.timerInterval) clearInterval(state.timerInterval);
    state.timerInterval = null;
  }

  async function openApp() {
    els.root.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    els.expired.classList.add('hidden');
    await fetchConfig();
    updatePricingUI();

    state.profile = loadProfile();
    const status = await syncSessionStatus({ apply: false });

    const resumePhases = ['free', 'paid', 'pending_verification', 'free_expired', 'locked', 'paid_expired'];
    const canResumeChat =
      status &&
      hasCompleteProfile(state.profile) &&
      (status.allowed || resumePhases.includes(status.phase) || status.paymentStatus === 'pending');

    if (canResumeChat) {
      showView('chat');
      applySessionStatus(status, { syncMessages: true });
      startStatusPolling();
      state.sessionReady = true;
      return;
    }

    showView('form');
    prefillFormFromProfile();
  }

  function prefillFormFromProfile() {
    const p = state.profile || loadProfile();
    if (!p || !els.form) return;
    const set = (name, val) => {
      const el = els.form.querySelector(`[name="${name}"]`);
      if (el && val) el.value = val;
    };
    set('fullName', p.fullName);
    set('dateOfBirth', p.dateOfBirth);
    set('timeOfBirth', p.timeOfBirth);
    set('placeOfBirth', p.placeOfBirth);
    set('phone', p.phone);
    set('email', p.email);
    set('concern', p.concern);
  }

  function closeApp() {
    stopStatusPolling();
    els.root.classList.remove('is-open');
    document.body.style.overflow = '';
    els.expired.classList.add('hidden');
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && els.root.classList.contains('is-open')) closeApp();
  }

  async function fetchConfig() {
    try {
      const res = await fetch(`${API_BASE}/config`);
      if (res.ok) {
        const apiConfig = await tryReadJsonResponse(res);
        if (!apiConfig) return;
        state.config = {
          ...state.config,
          ...apiConfig,
          // Theme settings take precedence for payment display
          sessionPriceInr: Number(THEME_CFG.sessionPriceInr) || apiConfig.sessionPriceInr || 100,
          freeTrialMinutes: Number(THEME_CFG.freeTrialMinutes) || apiConfig.freeTrialMinutes || 5,
          paidSessionMinutes: Number(THEME_CFG.paidSessionMinutes) || apiConfig.paidSessionMinutes || 10,
          upiQrImageUrl: THEME_UPI_QR_URL || state.config.upiQrImageUrl || '',
          upiId: THEME_CFG.upiId || state.config.upiId || '',
        };
        state.config.totalSessionMinutes =
          state.config.freeTrialMinutes + state.config.paidSessionMinutes;
      }
    } catch {
      /* use theme defaults */
    }
  }

  function loadProfile() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE.profile) || 'null');
    } catch {
      return null;
    }
  }

  function onFormSubmit(e) {
    e.preventDefault();
    const fd = new FormData(els.form);
    state.profile = {
      fullName: String(fd.get('fullName') || '').trim(),
      dateOfBirth: String(fd.get('dateOfBirth') || '').trim(),
      timeOfBirth: String(fd.get('timeOfBirth') || '').trim(),
      placeOfBirth: String(fd.get('placeOfBirth') || '').trim(),
      phone: String(fd.get('phone') || '').trim(),
      email: String(fd.get('email') || '').trim(),
      concern: fd.get('concern') || '',
    };
    if (!hasCompleteProfile(state.profile)) {
      alert('Please fill in your name, date of birth, time of birth, phone, and email.');
      return;
    }
    sessionStorage.setItem(STORAGE.profile, JSON.stringify(state.profile));

    state.sessionId = crypto.randomUUID();
    localStorage.setItem(STORAGE.session, state.sessionId);
    state.messages = [];
    state.welcomeSent = false;
    state.sessionReady = false;
    localStorage.removeItem(STORAGE.messages);
    if (els.messages) els.messages.innerHTML = '';

    startChatSession();
  }

  async function ensureSessionStarted() {
    if (!state.profile) state.profile = loadProfile();
    const res = await fetch(`${API_BASE}/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: state.sessionId, birthDetails: state.profile }),
    });
    const data = await readJsonResponse(res);
    if (!res.ok) {
      throw new Error(data.message || data.error || 'Could not start session');
    }
    state.sessionReady = true;
    applySessionStatus(data);
    return data;
  }

  function togglePaymentDetails() {
    els.paymentForm?.classList.toggle('hidden');
    const btn = els.root.querySelector('[data-action="toggle-payment-details"]');
    if (btn && els.paymentForm) {
      btn.textContent = els.paymentForm.classList.contains('hidden')
        ? 'Add UTR / screenshot (optional, faster verification)'
        : 'Hide UTR / screenshot form';
    }
  }

  async function onPaymentDone() {
    const btn = els.paymentDoneBtn;
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(`${API_BASE}/payment/done`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: state.sessionId }),
      });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Could not confirm payment');

      if (data.session) applySessionStatus(data.session);
      await preparePaymentOverlay('pending');
      startStatusPolling();
    } catch (err) {
      alert(err.message || 'Could not submit payment confirmation.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function onPaymentSubmit(e) {
    e.preventDefault();
    const btn = els.root.querySelector('#lc-astro-ai-submit-payment');
    const fd = new FormData(els.paymentForm);
    const utr = String(fd.get('utrReference') || '').trim();
    const screenshot = fd.get('screenshot');

    if (!utr) {
      alert('Please enter UTR/reference number, or tap Payment Done if you already paid.');
      return;
    }
    if (!screenshot || !screenshot.size) {
      alert('Please upload a payment screenshot, or tap Payment Done for manual verification.');
      return;
    }

    const body = new FormData();
    body.append('sessionId', state.sessionId);
    body.append('utrReference', utr);
    body.append('screenshot', screenshot);

    if (btn) btn.disabled = true;
    try {
      const res = await fetch(`${API_BASE}/payment/submit`, { method: 'POST', body });
      const data = await readJsonResponse(res);
      if (!res.ok) throw new Error(data.message || data.error || 'Payment submission failed');

      applySessionStatus(data.session || { phase: 'pending_verification', paymentStatus: 'pending', allowed: false });
      await preparePaymentOverlay('pending');
      startStatusPolling();
      els.paymentForm.reset();
    } catch (err) {
      alert(err.message || 'Could not submit payment.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function startFreeChat() {
    if (state.initInProgress) return;
    state.initInProgress = true;

    showView('chat');
    state.profile = loadProfile();
    els.expired.classList.add('hidden');
    setChatLocked(false);

    try {
      const peek = await syncSessionStatus({ apply: false });
      if (
        peek &&
        !peek.allowed &&
        (peek.phase === 'pending_verification' || peek.phase === 'free_expired' || peek.paymentStatus === 'pending')
      ) {
        applySessionStatus(peek, { syncMessages: true });
        startStatusPolling();
        return;
      }

      const data = await ensureSessionStarted();
      applySessionStatus(data, { syncMessages: true });
      startStatusPolling();

      const hasHistory = (data.messages && data.messages.length) || hasAssistantWelcome();
      if (!hasHistory && data.allowed) {
        await requestAiReply([], { isInit: true, sessionVerified: true });
      }
    } catch (err) {
      const msg =
        err.code === 'advisor_offline' ? ADVISOR_OFFLINE_MSG : err.message || ERROR_FALLBACK;
      alert(msg);
      showView('form');
    } finally {
      state.initInProgress = false;
    }
  }

  async function startChatSession() {
    await startFreeChat();
  }

  function addGemstoneCards(gemstones, afterMsgId) {
    if (!gemstones?.length) return;
    const anchor = afterMsgId
      ? els.messages.querySelector(`[data-msg-id="${afterMsgId}"]`)
      : null;
    const wrap = document.createElement('div');
    wrap.className = 'lc-astro-ai__gem-cards';

    gemstones.forEach((g) => {
      const base = (state.config.shopBaseUrl || window.location.origin || '').replace(/\/$/, '');
      const url =
        g.shopUrl?.startsWith('http') ? g.shopUrl : `${base}${g.shopUrl || '/collections/' + (g.handle || '')}`;
      const card = document.createElement('article');
      card.className = 'lc-astro-ai__gem-card';
      card.innerHTML = `
        <div class="lc-astro-ai__gem-card-icon">💎</div>
        <div class="lc-astro-ai__gem-card-body">
          <h4>${escapeHtml(g.name)}</h4>
          <p>${escapeHtml(g.benefit || '')}</p>
          ${g.metal ? `<span class="lc-astro-ai__gem-meta">${escapeHtml(g.metal)} · ${escapeHtml(g.finger || '')}${g.day ? ' · ' + escapeHtml(g.day) : ''}</span>` : ''}
          <a href="${escapeHtml(url)}" class="lc-astro-ai__gem-buy">
            Shop ${escapeHtml(g.name)} on Living Crystals →
          </a>
        </div>
      `;
      wrap.appendChild(card);
    });

    if (anchor && anchor.nextSibling) {
      els.messages.insertBefore(wrap, anchor.nextSibling);
    } else {
      els.messages.appendChild(wrap);
    }
    scrollToBottom();
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  function addTyping() {
    removeTyping();
    const row = document.createElement('div');
    row.className = 'lc-astro-ai__msg-row lc-astro-ai__msg-row--assistant';
    row.id = 'lc-astro-ai-typing';
    row.innerHTML = `${avatarImg('lc-astro-ai__avatar-img--bubble', 'Acharya Ji')}<div class="lc-astro-ai__bubble lc-astro-ai__bubble--assistant lc-astro-ai__typing">Acharya Ji is thinking…</div>`;
    els.messages.appendChild(row);
    scrollToBottom();
  }

  function removeTyping() {
    document.getElementById('lc-astro-ai-typing')?.remove();
  }

  function buildChatPayload(extraMessages, opts = {}) {
    const history = state.messages
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content.trim() }));

    const extra = (extraMessages || []).filter(
      (m) => m && (m.role === 'user' || m.role === 'assistant') && m.content && m.content.trim()
    );

    return {
      messages: opts.isInit ? [] : history.concat(extra),
      sessionId: state.sessionId,
      birthDetails: state.profile,
      isInit: !!opts.isInit,
    };
  }

  async function ensureSessionForChatRequest() {
    if (state.sessionReady) return true;
    try {
      await ensureSessionStarted();
      return true;
    } catch (err) {
      console.error('[Astro AI] session ensure failed:', err);
      if (err.code === 'advisor_offline') throw err;
      return false;
    }
  }

  async function postChatWithSessionRetry(url, payload, opts = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (opts.stream) headers.Accept = 'text/event-stream';

    let res = await fetchWithTimeout(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await readJsonResponse(res.clone());
    } catch (err) {
      if (err.code === 'advisor_offline') {
        const offlineErr = new Error(ADVISOR_OFFLINE_MSG);
        offlineErr.code = 'advisor_offline';
        throw offlineErr;
      }
      data = null;
    }

    const needsSessionRecovery =
      res.status === 404 &&
      data &&
      (data.code === 'session_not_found' || /session not found/i.test(data.message || ''));

    if (needsSessionRecovery) {
      const ok = await ensureSessionForChatRequest();
      if (ok) {
        res = await fetchWithTimeout(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
      }
    }

    return res;
  }

  async function fetchWithTimeout(url, options, timeoutMs = CHAT_TIMEOUT_MS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  async function consumeStreamResponse(res, onDelta) {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let donePayload = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data:')) continue;
        try {
          const data = JSON.parse(line.slice(5).trim());
          if (data.type === 'delta' && data.content) onDelta(data.content);
          if (data.type === 'done') donePayload = data;
          if (data.type === 'error') throw new Error(data.message || ERROR_FALLBACK);
        } catch (parseErr) {
          if (parseErr.message === ERROR_FALLBACK) throw parseErr;
        }
      }
    }

    return donePayload;
  }

  function applyChatResponse(data, assistantMsgId) {
    if (data.meta?.syncOnly || (data.meta?.resumed && !data.reply)) {
      return null;
    }

    if (data.shopBaseUrl) {
      state.config.shopBaseUrl = data.shopBaseUrl.replace(/\/$/, '');
    }

    if (data.meta?.resumed) {
      const existing = state.messages.filter((m) => m.role === 'assistant').slice(-1)[0];
      if (existing) return existing;
    }

    if (!data.reply) return null;

    if (assistantMsgId && findMessageById(assistantMsgId)) {
      updateMessageContent(assistantMsgId, data.reply);
      const msg = findMessageById(assistantMsgId);
      addGemstoneCards(data.gemstones, assistantMsgId);
      persistMessagesCache();
      return msg;
    }

    const msg = appendMessage(createMessage('assistant', data.reply));
    addGemstoneCards(data.gemstones, msg?.id);
    return msg;
  }

  async function requestAiReply(extraMessages, opts = {}) {
    if (state.chatLocked || state.isSending) return;

    let sessionOk = opts.sessionVerified;
    if (!sessionOk) {
      try {
        sessionOk = await ensureSessionForChatRequest();
      } catch (err) {
        removeTyping();
        appendMessage(
          createMessage(
            'assistant',
            err.code === 'advisor_offline' ? ADVISOR_OFFLINE_MSG : ERROR_FALLBACK
          )
        );
        return;
      }
    }
    if (!sessionOk) {
      appendMessage(createMessage('assistant', ERROR_FALLBACK));
      return;
    }

    const requestId = crypto.randomUUID();
    state.isSending = true;
    state.activeRequestId = requestId;
    updateSendButtonState();
    addTyping();

    const payload = buildChatPayload(extraMessages, opts);
    const useStream = state.config.streamingEnabled && !opts.isInit;
    const streamUrl = `${API_BASE}/chat/stream`;
    const chatUrl = `${API_BASE}/chat`;

    let assistantMsgId = null;
    let streamStarted = false;

    try {
      if (useStream) {
        try {
          const res = await postChatWithSessionRetry(streamUrl, payload, { stream: true });

          if (res.ok && res.headers.get('content-type')?.includes('text/event-stream')) {
            removeTyping();
            const placeholder = appendMessage(createMessage('assistant', ''));
            assistantMsgId = placeholder.id;
            streamStarted = true;
            let streamed = '';

            const donePayload = await consumeStreamResponse(res, (delta) => {
              streamed += delta;
              updateMessageContent(assistantMsgId, streamed);
            });

            if (donePayload) {
              applyChatResponse(donePayload, assistantMsgId);
              if (donePayload.session) applySessionStatus(donePayload.session);
              return;
            }

            if (streamed) {
              updateMessageContent(assistantMsgId, streamed);
              persistMessagesCache();
              return;
            }
          } else if (res.ok) {
            const data = await readJsonResponse(res);
            removeTyping();
            applyChatResponse(data, null);
            if (data.session) applySessionStatus(data.session);
            return;
          }
        } catch (streamErr) {
          if (streamStarted && assistantMsgId) {
            state.messages = state.messages.filter((m) => m.id !== assistantMsgId);
            els.messages.querySelector(`[data-msg-id="${assistantMsgId}"]`)?.remove();
          }
          /* fall through to regular POST */
        }
      }

      const res = await postChatWithSessionRetry(chatUrl, payload);
      let data;
      try {
        data = await readJsonResponse(res);
      } catch (parseErr) {
        removeTyping();
        appendMessage(
          createMessage(
            'assistant',
            parseErr.code === 'advisor_offline' ? ADVISOR_OFFLINE_MSG : ERROR_FALLBACK
          )
        );
        return;
      }
      removeTyping();

      if (res.status === 403 && data.error === 'chat_locked') {
        applySessionStatus(data);
        return;
      }

      if (!res.ok) {
        appendMessage(createMessage('assistant', ERROR_FALLBACK));
        console.error('[Astro AI] chat error:', data);
        return;
      }

      applyChatResponse(data, assistantMsgId);
      if (data.session) applySessionStatus(data.session);
    } catch (err) {
      removeTyping();
      if (assistantMsgId) {
        state.messages = state.messages.filter((m) => m.id !== assistantMsgId);
        els.messages.querySelector(`[data-msg-id="${assistantMsgId}"]`)?.remove();
      }
      appendMessage(
        createMessage(
          'assistant',
          err.code === 'advisor_offline' ? ADVISOR_OFFLINE_MSG : ERROR_FALLBACK
        )
      );
      console.error('[Astro AI]', err);
    } finally {
      if (state.activeRequestId === requestId) {
        state.isSending = false;
        state.activeRequestId = null;
      }
      updateSendButtonState();
      els.input?.focus();
    }
  }

  async function onChatSubmit(e) {
    e.preventDefault();
    if (state.chatLocked || state.isSending) return;

    const text = els.input.value.trim();
    if (!text) return;

    els.input.value = '';
    updateSendButtonState();

    appendMessage(createMessage('user', text));
    await requestAiReply([], {});
  }

  function isAstroChatHash(hash) {
    return hash === '#lc-astro-chat' || hash === '#ask-acharya-ji';
  }

  function bindTriggers() {
    document.querySelectorAll('[data-lc-astro-chat-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openApp();
      });
    });

    document.addEventListener('click', (e) => {
      const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href === '#lc-astro-chat' || href === '#ask-acharya-ji' || href.endsWith('#lc-astro-chat') || href.endsWith('#ask-acharya-ji')) {
        e.preventDefault();
        openApp();
      }
    });
  }

  function bindHashOpen() {
    if (isAstroChatHash(window.location.hash)) {
      openApp();
    }
    window.addEventListener('hashchange', () => {
      if (isAstroChatHash(window.location.hash)) {
        openApp();
      }
    });
  }

  function init() {
    createApp();
    bindTriggers();
    bindHashOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
